/**
 * Helper SEO yang dipakai ulang di semua halaman publik:
 * title, description, Open Graph, canonical, dan JSON-LD.
 */

export interface OpsiSeo {
  judul: string
  deskripsi: string
  /** Path absolut halaman, diawali "/" — dipakai untuk canonical & og:url. */
  path: string
  /** Blok JSON-LD siap pakai (schema.org). */
  jsonLd?: Record<string, unknown>[]
  /** Halaman transaksional: jangan diindeks. */
  noindex?: boolean
  /**
   * Nama berkas gambar OG di `public/og/` (mis. 'og-harga.png'). Kosongkan
   * saja — dipilih otomatis dari `path`. Isi hanya kalau halaman baru butuh
   * gambar sendiri yang belum masuk peta di bawah.
   */
  gambar?: string
}

/**
 * Prefiks rute yang TIDAK BOLEH diindeks, apa pun yang terjadi.
 *
 * Dua kelompok, dua alasan:
 * - `/app`, `/redeem`, `/checkout` — halaman kerja di balik token. Tidak punya
 *   nilai pencarian, dan URL-nya sering memuat konteks pembelian seseorang.
 * - `/legacy` — cadangan desain lama. Isinya duplikat kata-per-kata dari
 *   halaman utama, jadi kalau terindeks ia bersaing melawan aslinya sendiri.
 *
 * Daftar ini adalah SATU-SATUNYA sumber kebenaran; `_headers` (lapisan HTTP)
 * dan routeRules di nuxt.config.ts menyalinnya. Kalau menambah rute
 * transaksional baru, tambahkan di sini lebih dulu.
 */
export const PREFIKS_TANPA_INDEKS = ['/app', '/redeem', '/checkout', '/unduh', '/agen', '/legacy'] as const

/** true bila `path` termasuk rute yang tidak boleh diindeks. */
export function tanpaIndeks(path: string): boolean {
  return PREFIKS_TANPA_INDEKS.some((p) => path === p || path.startsWith(p + '/'))
}

export function useSitusUrl(): string {
  const config = useRuntimeConfig()
  return String(config.public.siteUrl || '').replace(/\/$/, '')
}

/** Ukuran kanvas semua gambar OG. Lihat scripts/og/buat-og.mjs. */
export const OG_LEBAR = 1200
export const OG_TINGGI = 630

/**
 * Peta path → gambar OG. Awalan path yang cocok paling PANJANG yang menang,
 * sehingga '/kompres-foto/500-kb' ikut memakai gambar alat kompres foto.
 */
const petaGambarOg: [string, string][] = [
  ['/berkaskilat', 'og-berkaskilat.png'],
  ['/rapikontak', 'og-rapikontak.png'],
  ['/kompres-foto', 'og-kompres-foto.png'],
  ['/kompres-pdf', 'og-kompres-pdf.png'],
  ['/harga', 'og-harga.png'],
  ['/buku', 'og-buku.png']
]

/**
 * Pilih gambar OG untuk sebuah path.
 *
 * KENAPA DIPUTUSKAN DI SINI, bukan diserahkan ke tiap halaman: sebelum ini
 * TIDAK ADA satu pun halaman yang punya og:image, jadi setiap tautan yang
 * di-share di WhatsApp — kanal distribusi utama produk ini — tampil polos.
 * Menaruh keputusannya di composable bersama berarti halaman baru mendapat
 * gambar merek secara otomatis; lupa memasang tidak mungkin lagi terjadi.
 *
 * Halaman /legacy/* memakai gambar padanan halaman aslinya: awalan '/legacy'
 * dipotong dulu. Berkas di /legacy sendiri tidak perlu diubah sama sekali.
 */
export function gambarOgUntuk(path: string): string {
  const bersih = path.replace(/^\/legacy/, '') || '/'
  const cocok = petaGambarOg
    .filter(([awalan]) => bersih === awalan || bersih.startsWith(awalan + '/'))
    .sort((a, b) => b[0].length - a[0].length)[0]
  return cocok ? cocok[1] : 'og-default.png'
}

export function pakaiSeo(o: OpsiSeo) {
  const situs = useSitusUrl()
  const url = situs + o.path

  // Rute di PREFIKS_TANPA_INDEKS TIDAK PERNAH boleh diindeks. Dipaksa di sini,
  // bukan diserahkan ke tiap halaman, supaya tidak ada yang lupa dan tidak ada
  // yang bisa membatalkannya dengan memanggil pakaiSeo() tanpa `noindex: true`.
  const noindex = o.noindex || tanpaIndeks(o.path)

  // og:image WAJIB URL absolut: WhatsApp, Facebook, dan X mengambilnya dari
  // servernya sendiri, bukan dari peramban pengunjung, jadi path relatif tidak
  // pernah ter-resolve dan kartunya kembali polos.
  const gambar = `${situs}/og/${o.gambar || gambarOgUntuk(o.path)}`

  useSeoMeta({
    title: o.judul,
    description: o.deskripsi,
    ogTitle: o.judul,
    ogDescription: o.deskripsi,
    ogUrl: url,
    ogType: 'website',
    ogLocale: 'id_ID',
    ogSiteName: 'tools.coreasia.id',
    ogImage: gambar,
    ogImageWidth: OG_LEBAR,
    ogImageHeight: OG_TINGGI,
    ogImageType: 'image/png',
    // Alt memakai judul halaman: itu memang teks besar yang tercetak di gambar.
    ogImageAlt: o.judul,
    twitterCard: 'summary_large_image',
    twitterTitle: o.judul,
    twitterDescription: o.deskripsi,
    twitterImage: gambar,
    twitterImageAlt: o.judul,
    robots: noindex ? 'noindex, nofollow' : 'index, follow'
  })

  useHead({
    link: [{ rel: 'canonical', href: url }],
    script: (o.jsonLd || []).map((blok) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(blok)
    }))
  })
}

/** Satu pertanyaan FAQ. */
export interface Faq {
  tanya: string
  jawab: string
}

/** Bangun blok JSON-LD FAQPage. Pakai maksimal satu per halaman. */
export function jsonLdFaq(daftar: Faq[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: daftar.map((f) => ({
      '@type': 'Question',
      name: f.tanya,
      acceptedAnswer: { '@type': 'Answer', text: f.jawab }
    }))
  }
}

/** Bangun blok JSON-LD BreadcrumbList dari pasangan [nama, path]. */
export function jsonLdRemah(situs: string, jejak: [string, string][]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: jejak.map(([nama, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: nama,
      item: situs + path
    }))
  }
}

export interface OpsiAplikasi {
  nama: string
  deskripsi: string
  url: string
  /** Harga dalam rupiah; 0 berarti gratis. */
  harga: number
}

/** Bangun blok JSON-LD SoftwareApplication + Offer. */
export function jsonLdAplikasi(o: OpsiAplikasi): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: o.nama,
    description: o.deskripsi,
    url: o.url,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web',
    inLanguage: 'id-ID',
    offers: {
      '@type': 'Offer',
      price: String(o.harga),
      priceCurrency: 'IDR',
      availability: 'https://schema.org/InStock'
    }
  }
}

export interface OpsiBuku {
  judul: string
  deskripsi: string
  url: string
  gambar: string
  penulis: string
  penerbit: string
  halaman: number
  isbn?: string
  /** Harga dalam rupiah. */
  harga: number
}

/**
 * Bangun blok JSON-LD Book + Offer.
 *
 * `bookFormat: EBook` dan `isAccessibleForFree: false` bukan hiasan: keduanya
 * yang membedakan halaman ini dari halaman buku cetak di mata mesin pencari,
 * dan mencegah cuplikan hasil pencarian menjanjikan unduhan gratis atas
 * barang berbayar.
 *
 * ISBN sengaja opsional. Menuliskan nomor yang tidak dimiliki lebih buruk
 * daripada tidak mencantumkannya sama sekali.
 */
export function jsonLdBuku(o: OpsiBuku): Record<string, unknown> {
  const blok: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: o.judul,
    description: o.deskripsi,
    url: o.url,
    image: o.gambar,
    bookFormat: 'https://schema.org/EBook',
    numberOfPages: o.halaman,
    inLanguage: 'id-ID',
    isAccessibleForFree: false,
    author: { '@type': 'Person', name: o.penulis },
    publisher: { '@type': 'Organization', name: o.penerbit },
    offers: {
      '@type': 'Offer',
      price: String(o.harga),
      priceCurrency: 'IDR',
      availability: 'https://schema.org/InStock',
      url: o.url,
      seller: { '@type': 'Organization', name: o.penerbit }
    }
  }
  if (o.isbn) blok.isbn = o.isbn
  return blok
}
